import { z } from "zod";
import { isValidTimezone } from "../cron/cronUtils";
import { canonicalTimeZone } from "../time/canonicalZone";

/**
 * A person's display preferences (round 3, 2026-09-30). The owner: "we should store the user
 * timezone and display the values aligned to it". One zone per person across every tenant and
 * instance (platform table `user_preferences`); every instant the app renders follows it. None is
 * stored until the person picks one: until then, and after they pick "Use this device's time
 * zone", every screen follows the device they are on (owner decision, 2026-09-30).
 */

/**
 * An IANA zone name `Intl` renders with ("UTC", "Asia/Kolkata", "Etc/GMT+5"). An offset string
 * ("+05:30") is refused although newer engines accept one: it is not a zone, it has no daylight
 * rules and no name to show. "UTC+5" is refused by `Intl` itself.
 */
export function isIanaTimeZone(name: string): boolean {
  if (!/^[A-Za-z]/.test(name)) return false;
  return isValidTimezone(name);
}

const ZONE_MESSAGE = "Choose a time zone such as Asia/Kolkata or UTC.";

/**
 * An old IANA name is taken and its current name is what goes on (`Asia/Calcutta` is stored as
 * `Asia/Kolkata`): Chromium lists and reports the old names (`shared/time/canonicalZone.ts`).
 */
export const displayTimeZoneSchema = z
  .string()
  .max(64)
  .refine(isIanaTimeZone, ZONE_MESSAGE)
  .transform(canonicalTimeZone);

/**
 * `PUT /me/preferences`: the caller's own zone, or null to follow this device again. No default:
 * the key is required. This is the only write: nothing stores a zone the person did not choose
 * (owner decision, 2026-09-30).
 */
export const putUserPreferencesBodySchema = z
  .object({ timezone: displayTimeZoneSchema.nullable() })
  .strict();
export type PutUserPreferencesBody = z.infer<typeof putUserPreferencesBodySchema>;

/**
 * `PUT /me/preferences/last-instance` (round 4, 2026-09-30): the instance the caller just switched
 * to, stored so a sign-in on any browser lands there. The server takes only an instance the
 * caller's `GET /me` lists; there is no clearing it (a revoked or deleted instance drops out on
 * its own).
 */
export const putLastInstanceBodySchema = z.object({ instanceId: z.uuid() }).strict();
export type PutLastInstanceBody = z.infer<typeof putLastInstanceBodySchema>;

/** What `PUT /me/preferences/last-instance` answers. */
export interface LastInstanceView {
  lastInstanceId: string | null;
}

/**
 * What `GET /me` carries under `preferences`. `timezone` null means "this device's zone", read on
 * every load. `lastInstanceId` (round 4) is the instance the person last opened (the switcher,
 * the picker page, or since round 6 T-08 the landing's own pick), null when
 * none is stored or the person can no longer reach it; optional so a fixture or an older server
 * without it still parses. `PUT /me/preferences` answers `{ timezone }` alone.
 */
export const userPreferencesViewSchema = z.object({
  timezone: z.string().nullable(),
  lastInstanceId: z.string().nullable().optional(),
});
export type UserPreferencesView = z.infer<typeof userPreferencesViewSchema>;
